import React, { useEffect, useState, useCallback, useMemo } from "react";
import AddRoleModal from "./AddRoleModal";
import "../../../css/RolesPage.css"; // Imports the refactored CSS
import ViewRoleModal from "./ViewRoleModal";
import api from '../../../api/axiosConfig';

// ... (Interface Role and Form remain the same) ...
interface Form {
  _id: string;
  name: string;
  category?: string;
}

interface Role {
  _id: string;
  name: string;
  type: string;
  details: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  disabledOn?: string | null;
  formIds: Form[];
  parent?: string | null;
  subRoles?: number; // This will hold the count
}

const RoleTable: React.FC = () => {
  // ... (All your state and logic remains the same) ...
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const fetchRoles = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) return;
      const res = await api.auth_api.get("/roles", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const topLevelRoles: Role[] = res.data.filter(
        (role: Role) => role.parent === null
      );
      const rolesWithSubRoleCounts = await Promise.all(
        topLevelRoles.map(async (role) => {
          try {
            const subRolesRes = await api.auth_api.get(
              `/roles/${role._id}/subroles`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            return { ...role, subRoles: subRolesRes.data.length };
          } catch (err) {
            console.error(`Error fetching sub-roles for ${role.name}:`, err);
            return { ...role, subRoles: 0 };
          }
        })
      );
      setRoles(rolesWithSubRoleCounts);
    } catch (err) {
      console.error("❌ Error fetching roles:", err);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const filteredRoles = useMemo(() => {
    return roles
      .filter(role => {
        if (!searchTerm) return true;
        return role.name.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .filter(role => {
        if (statusFilter === 'All') return true;
        const isActive = statusFilter === 'Active';
        return role.isActive === isActive;
      })
      .filter(role => {
        if (categoryFilter === 'All') return true;
        return role.type === categoryFilter;
      });
  }, [roles, searchTerm, statusFilter, categoryFilter]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, categoryFilter]);

  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRolesToDisplay = useMemo(() => {
    return filteredRoles.slice(startIndex, endIndex);
  }, [filteredRoles, startIndex, endIndex]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRole(null);
  };


  return (
    <div className="roles-page-table-container"> {/* Renamed */}
      <div className="roles-page-table-header"> {/* Renamed */}
        <div className="global-filters-container">
          <div className="global-search-wrapper">
            <svg
              className="global-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search by role name..."
              className="global-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select className="global-select-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <select className="global-select-input" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="All">All Types</option>
            <option value="QAVACH STAFF">QAVACH STAFF</option>
            <option value="TELE SPECIALIST">TELE SPECIALIST</option>
          </select>
        </div>
        <button className="roles-page-add-btn" onClick={() => setIsModalOpen(true)}> {/* Renamed */}
          + Add New Role
        </button>
      </div>

      <table className="roles-page-table"> {/* Renamed */}
        <thead>
          <tr>
            <th>Role Name</th>
            <th>Category</th>
            <th>Forms Assigned</th>
            <th>Created On</th>
            <th>Disabled On</th>
            <th>Sub-Roles</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {currentRolesToDisplay.length > 0 ? (
            currentRolesToDisplay.map((role) => (
              <tr
                key={role._id}
                onClick={() => {
                  setSelectedRole(role);
                  setIsModalOpen(true);
                }}
                style={{ cursor: "pointer" }}
              >
                <td>{role.name}</td> {/* Removed .link class, add if needed in CSS */}
                <td>
                  {/* Renamed classes */}
                  <span className={`roles-page-badge ${role.type === "QAVACH STAFF" ? "roles-page-badge-qavach" : "roles-page-badge-tele"}`}>
                    {role.type}
                  </span>
                </td>
                <td>{role.formIds?.length || 0} Forms</td>
                <td>{role.createdAt ? new Date(role.createdAt).toLocaleDateString() : "-"}</td>
                <td>{role.disabledOn ? new Date(role.disabledOn).toLocaleDateString() : "-"}</td>
                <td>{role.subRoles ?? 0}</td>
                <td>
                  {/* Renamed classes */}
                  <span className={`roles-page-status ${role.isActive ? "roles-page-status-active" : "roles-page-status-inactive"}`}>
                    {role.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} style={{ textAlign: "center" }}>
                No roles match the current filters
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ... (Modals remain the same) ... */}
      <AddRoleModal
        isOpen={isModalOpen && !selectedRole}
        onClose={handleCloseModal}
        onCreate={(newRole) => {
          setRoles((prev) => [...prev, newRole]);
          handleCloseModal();
        }}
      />
      {isModalOpen && selectedRole && (
        <ViewRoleModal
          roleId={selectedRole._id}
          onClose={handleCloseModal}
          onUpdate={(updatedRole) => {
            setRoles((prev) =>
              prev.map((r) => (r._id === updatedRole._id ? { ...r, ...updatedRole } : r))
            );
            handleCloseModal();
          }}
        />
      )}

      {totalPages > 0 && (
        <div className="pagination-footer" style={{ marginTop: '20px' }}>
          <p>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredRoles.length)} of {filteredRoles.length} results
          </p>
          <div className="pagination">
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
              <button key={pageNumber} onClick={() => setCurrentPage(pageNumber)} className={currentPage === pageNumber ? "active" : ""}>
                {pageNumber}
              </button>
            ))}
            <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleTable;